import React from 'react';
import { Grid, List, ListItem, ListItemText, ListSubheader, makeStyles, Theme, useTheme } from '@material-ui/core';
import { WarningRounded, DoneRounded } from '@material-ui/icons';
import { StyledText } from '../StyledText';
import { Publishing } from './publishingLogic';
import { EntrepreneurProfileBody, InvestorProfile, Role } from '../../types/types';

interface StyleProps {
  publishable: boolean
}
const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  root: {
    margin: theme.spacing(0),
    borderRadius: '8px',
    padding: 25,
    border: ((props) => `1px solid ${props.publishable ? theme.palette.secondary.main : '#FBBC05'}`),
    backgroundColor: '#FFF'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start'
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start'
  },
  listWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginLeft: '19px',
    margintTop: '20px',

    '& .MuiList-root': {
      marginRight: '80px',
      padding: '15px 0px',

      '& .MuiListSubheader-root': {
        padding: 0,
        color: theme.palette.text.primary
      },

      '& .MuiListItem-gutters': {
        padding: 0,

        '& .MuiListItemText-root': {
          margin: 0,
          marginRight: '4px'
        }
      },

      '& .MuiListItem-root': {
        paddingBottom: '4px',
        '& p:hover': {
          cursor: 'pointer',
          fontWeight: 500
        },
        textTransform: 'capitalize'
      }
    }

  },
  text: {
    marginLeft: '19px',
    marginRight: '20px',
    whiteSpace: 'normal'
  },
  button: {
    marginLeft: '20px',
    whiteSpace: 'nowrap'
  },
  link: {
    '& p :hover': {
      fontWeight: 500
    }
  }
}));

const getConfig = (role: Role, profile: EntrepreneurProfileBody | InvestorProfile) => {
  const { getPublishingGuideConfigForEnt, getPublishingGuideConfigForInvestor } = Publishing;
  // eslint-disable-next-line no-nested-ternary
  if (role === Role.Ent) {
    const { message, publishable, status } = getPublishingGuideConfigForEnt(profile as EntrepreneurProfileBody);
    return { message, publishable, status };
  } else {
    const { message, publishable, status } = getPublishingGuideConfigForInvestor(profile as InvestorProfile);
    return { message, publishable, status };
  }
};

interface GuideContentProps {
  status: Map<string, boolean>
  handleClick: (clicked: string) => void
  publishable: boolean
}

const GuideContent: React.FC<GuideContentProps> = ({ status, handleClick, publishable }) => {
  const statusList = Object.entries(Object.fromEntries(status)).map((entry) => ({ name: entry[0], satisfied: entry[1] }));
  const incompleteReqs = statusList.filter((part) => (!part.satisfied));
  const completeReqs = statusList.filter((part) => (part.satisfied));
  const styles = useStyles({ publishable });

  const makeItem = (part: { name: string, satisfied: boolean }) => (
    <ListItem onClick={(part.satisfied) ? undefined : () => { handleClick(part.name); }} key={part.name}>
      {
        part.satisfied
          ? <ListItemText primaryTypographyProps={{ variant: 'body1' }} primary={part.name} />
          : <StyledText variant='link' color='primary' className={styles.link} style={{ whiteSpace: 'normal' }}>{part.name}</StyledText>
      }
      {
        part.satisfied ? <DoneRounded color='secondary' /> : null
      }
    </ListItem>
  );

  return (
    <div className={styles.listWrapper}>
      { incompleteReqs.length > 0
        && (
          <List dense subheader={<ListSubheader disableSticky>Incomplete</ListSubheader>}>
            { incompleteReqs.map((part) => (makeItem(part))) }
          </List>
        )}
      { completeReqs.length > 0
        && (
          <List dense subheader={<ListSubheader disableSticky>Completed</ListSubheader>}>
            { completeReqs.map((part) => (makeItem(part))) }
          </List>
        )}
    </div>
  );
};
interface PublishingGuideProps {
  profile: EntrepreneurProfileBody | InvestorProfile
  role: Role.Ent | Role.Investor
  handleClick: (clicked: string) => void
}

const PublishingGuide: React.FC<PublishingGuideProps> = ({
  profile,
  role,
  handleClick
}) => {
  const { message, publishable, status } = getConfig(role, profile);
  const styles = useStyles({ publishable });
  const theme = useTheme();

  const warningColor = publishable ? theme.palette.secondary.main : '#FBBC05';


  return (
    <Grid id='publishing-guide' container wrap='nowrap' direction='column' justify='center' className={styles.root}>
      <div className={styles.contentWrapper}>
        <WarningRounded htmlColor={warningColor} />
        <div className={styles.content}>
          <StyledText variant='body1' className={styles.text} style={{ whiteSpace: 'normal' }}>
            {message}
          </StyledText>
          <GuideContent status={status} handleClick={handleClick} publishable={publishable} />
        </div>
      </div>
    </Grid>
  );
};

export default PublishingGuide;
